import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, Output, ViewChild } from '@angular/core';
import { Language } from 'src/app/course/enums/language.enum';
import { ContentSubtitleModel } from 'src/app/course/models/content-translate-file.model';
import { environment } from 'src/environments/environment';
import { LanguageService } from 'src/app/services/language.service';
import { LanguageEnum } from '../../models/item.model';

@Component({
  selector: 'app-video-player',
  templateUrl: './video-player.component.html',
  styleUrls: ['./video-player.component.scss']
})
export class VideoPlayerComponent implements AfterViewInit {
  wasPaused = null;
  
  timeSkip: number = 10;
  playbackRate: number = 1;
  videoProgress: number = 0;

  totalTime: string = "";
  volumeLevel: string = "high";
  currentTime: string = "0:00";
  currentLabel: string = "off";
  currentTimeline: string = "";

  isRtl: boolean = true;
  isPlay: boolean = false;
  isIntro: boolean = true;
  forward: boolean = false;
  backward: boolean = false;
  isScrubbing: boolean = false;
  toggleControls: boolean = false;
  showProgressInfo: boolean = false;
  isProgressUpdated: boolean = false;
  
  translateLanguage = Language;
  contentResource =  environment.mediaUrl;
  
  @Input() title: string = "";
  @Input() progress: number = 0;
  @Input() subtitles: ContentSubtitleModel[] = [];
  @Input() videoSrc: string = "";
  @Input() hasScrubbing: boolean = true;
  @Output() next = new EventEmitter();
  @Output() timelineCheckOut = new EventEmitter();

  @ViewChild('video') videoPlayer!: ElementRef;
  @ViewChild('rightMenu') rightMenu!: ElementRef;
  @ViewChild('volumeSlider') volumeSlider!: ElementRef;
  @ViewChild('videoContainer') videoContainer!: ElementRef;
  @ViewChild('timelineContainer') timelineContainer!: ElementRef;
  
  leadingZeroFormatter = new Intl.NumberFormat(undefined, {minimumIntegerDigits: 2});

  constructor(private langService: LanguageService) { };

  ngAfterViewInit(): void {
    this.isRtl = this.langService.getCurrentLanguage() === LanguageEnum.ar;
    this.langService.languageChange.subscribe((result: LanguageEnum) => this.isRtl = result === LanguageEnum.ar);

    if (this.videoSrc && this.videoPlayer) {
      this.videoPlayer.nativeElement.src = this.videoSrc;
      this.videoPlayer.nativeElement.load();
      this.videoPlayer.nativeElement.autoplay = false;
    }
  }

  onRightClick(event: any): void {
    event.preventDefault();
    this.rightMenu.nativeElement.style.display = "block";
    this.rightMenu.nativeElement.style.top = event.clientY - 122 + "px";
    this.rightMenu.nativeElement.style.left = event.clientX - 131 + "px";
  }

  @HostListener("click", ["$event"])
  onClick(): void {
    this.rightMenu.nativeElement.style.display = "none";
  }

  // Document Events
  @HostListener('window:keydown', ['$event'])
  actionsVideo(event: any): void {
    const tagName = document.activeElement?.tagName.toLowerCase();
    if(tagName === "input") return;
    switch (event.key.toLocaleLowerCase()) {
      case " ":
      case "k":
        if(tagName === "button") return;
        this.togglePlay();
        break;
      case "f":
        this.toggleFullScreenMode();
        break;
      case "m":
        this.toggleMute();
        break;
      case "arrowleft":
      case "j":
        this.backward = true;
        this.skip(-this.timeSkip);
        break;
      case "arrowright":
      case "l":
        this.forward = true;
        this.skip(+this.timeSkip);
        break;
    }
  }

  // Pause | Play
  togglePlay(): void {
    this.isIntro = false;
    this.videoPlayer.nativeElement.paused ? this.videoPlayer.nativeElement.play() : this.videoPlayer.nativeElement.pause();
  }
  // Full Screen Mode
  toggleFullScreenMode(): void {
    if(document.fullscreenElement === null){
      this.videoContainer.nativeElement.requestFullscreen();
    } else document.exitFullscreen();
  }

  @HostListener('window:fullscreenchange', ['$event'])
  fullscreenchange(): void {
    this.videoContainer.nativeElement.classList.toggle("full-screen", document.fullscreenElement);
  }

  // Volume
  toggleMute(): void {
    this.videoPlayer.nativeElement.muted = !this.videoPlayer.nativeElement.muted;
  }

  slideVolume(): void {
    this.videoPlayer.nativeElement.volume = this.volumeSlider.nativeElement.value;
    this.videoPlayer.nativeElement.muted = this.volumeSlider.nativeElement.value === 0;
  }

  changeVolume(): void {
    this.volumeSlider.nativeElement.value = this.videoPlayer.nativeElement.volume;
    if(this.videoPlayer.nativeElement.muted || this.videoPlayer.nativeElement.volume === 0){
      this.volumeSlider.nativeElement.value = 0;
      this.volumeLevel = "muted";
    } else if(this.videoPlayer.nativeElement.volume >= .5) this.volumeLevel = "high";
    else this.volumeLevel = "low";
    this.videoContainer.nativeElement.dataset.volumeLevel = this.volumeLevel;
  }

  // Playback
  changePlaybackSpeed(rate: number): void {
    this.videoPlayer.nativeElement.playbackRate = rate;
    this.playbackRate = rate;
  }

  // Duration
  loadedVideo(): void {
    this.totalTime = this.formatDuration(this.videoPlayer.nativeElement.duration);
  }
  
  updateDuration(): void {
    const percent = this.videoPlayer.nativeElement.currentTime / this.videoPlayer.nativeElement.duration;
    this.currentTime = this.formatDuration(this.videoPlayer.nativeElement.currentTime);
    this.timelineContainer.nativeElement.style.setProperty("--progress-position", percent);
    this.videoProgress = +(percent * 100).toFixed(1);
    if(this.videoProgress >= this.progress && !this.isProgressUpdated){
      this.isProgressUpdated = true;
      this.timelineCheckOut.emit();
      this.toggleProgressAlert();
    }
  }

  toggleProgressAlert(): void {
    if(this.isProgressUpdated){
      this.toggleControls = true;
      this.showProgressInfo = true;
      setTimeout(() => {
        this.showProgressInfo = false;
        this.toggleControls = false;
      }, 5000);
    }
  }

  formatDuration(time: number): string {
    const seconds = Math.floor(time % 60);
    const minutes = Math.floor(time / 60) % 60;
    const hours = Math.floor(time / 3600);
    if(hours === 0) return `${minutes}:${this.leadingZeroFormatter.format(seconds)}`;
    else return `${hours}:${this.leadingZeroFormatter.format(minutes)}:${this.leadingZeroFormatter.format(seconds)}`;
  }

  skip(duration: number): void {
    this.videoPlayer.nativeElement.currentTime += duration;
    setTimeout(() => {
      this.forward = false;
      this.backward = false;
    }, 1000);
  }

  // Caption
  loadCaptions(): void {
    const tracks = this.videoPlayer.nativeElement.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].mode = "hidden";
    }
  }

  getCaptions(label: string): void {
    if(!label) return;
    const tracks = this.videoPlayer.nativeElement.textTracks;
    for (let i = 0; i < tracks.length; i++) {
      if (label === "off") {
        tracks[i].mode = "hidden";
        this.currentLabel = "off";
      } else if (tracks[i].label === label) {
        tracks[i].mode = "showing";
        this.currentLabel = label;
      } else {
        tracks[i].mode = "hidden";
      }
    }
  }

  getLanguageCode(lang: Language): string {
    return Language[lang].substring(0, 2).toLowerCase();
  }

  // Timeline
  handleTimelineUpdate(event: any): void {
    const rect = this.timelineContainer.nativeElement.getBoundingClientRect();
    const percent: number = Math.min(Math.max(0, event.x - rect.x), rect.width) / rect.width;
    this.timelineContainer.nativeElement.style.setProperty("--preview-position", percent);
    this.currentTimeline = this.formatDuration(percent * this.videoPlayer.nativeElement.duration);
    if(this.isScrubbing){
      event.preventDefault();
      this.timelineContainer.nativeElement.style.setProperty("--progress-position", percent);
    }
  }

  toggleScrubbing(event: any): void {
    if(this.hasScrubbing){
      const rect = this.timelineContainer.nativeElement.getBoundingClientRect();
      const percent = Math.min(Math.max(0, event.x - rect.x), rect.width) / rect.width;
      this.isScrubbing = (event.buttons & 1) === 1;
      this.videoContainer.nativeElement.classList.toggle("scrubbing", this.isScrubbing);
      if(this.isScrubbing){
        this.wasPaused = this.videoPlayer.nativeElement.paused;
        this.videoPlayer.nativeElement.pause();
      } else {
        this.videoPlayer.nativeElement.currentTime = percent * this.videoPlayer.nativeElement.duration;
        if(!this.wasPaused) this.videoPlayer.nativeElement.play();
      }
      this.handleTimelineUpdate(event);
    }
  }

  @HostListener('window:mouseup', ['$event'])
  updateScrubbing(event: any): void {
    if(this.isScrubbing) this.toggleScrubbing(event);
  }

  @HostListener('window:mousemove', ['$event'])
  updateTimeline(event: any): void {
    if(this.isScrubbing) this.handleTimelineUpdate(event);
  }

  showHideControls() {
    this.toggleControls = !this.toggleControls;
  }

  bufferedVideo(): void {
    if(!this.videoPlayer.nativeElement) return;
    let timeLoaded = 0;
    let timeTotal = 0;
    if( timeTotal == 0 ) timeTotal = this.videoPlayer.nativeElement.duration;
    if (this.videoPlayer.nativeElement.buffered.length === 1) {
      timeLoaded = this.videoPlayer.nativeElement.buffered.end(0);
      this.timelineContainer.nativeElement.style.display = "block";
      this.timelineContainer.nativeElement.style.setProperty("--preview-position", (timeLoaded / timeTotal));
    }
  }

  // Download
  downloadVideo(): void {
    const link = document.createElement('a');
    link.href = this.videoPlayer.nativeElement.currentSrc;
    link.download = this.title;
    link.target = "_blank";
    link.click();
  }
}
